import { ref } from 'vue';
import SignalRClient from '@/ts/SignalRClient';
import RoomVO from '@/ts/VO/RoomVO';
import KillPigConfigVO from '@/ts/VO/KillPigConfigVO';

type Settled<T> = { ok: true; value: T } | { ok: false; error: unknown };

/** 单个 Hub 调用失败不影响其它调用（避免一个接口异常把整面板状态卡死） */
function safeInvoke<T>(action: string): Promise<Settled<T>> {
  return SignalRClient.Instance.InvokeAsync<T>(action)
    .then((value) => ({ ok: true as const, value }))
    .catch((error) => ({ ok: false as const, error }));
}

/**
 * “全局控制”面板（QuickKillPanel）的数据与保存逻辑。
 *
 * 该面板只在账号详情页（AccountDetail）使用：详情页没有首页那套编辑态
 * （input / selectRooms / 未保存基线），因此这里提供轻量实现：
 * 直接读服务端权威状态（GetKillPigConfig + GetRoomList），保存时按
 * “先 SetKillPig（房间）再 SetKillPigConfig（总配置）”的顺序写回，与首页保存一致。
 */
export function useQuickKill() {
  /** 托管开关：至少有一间开启即为 true（全关才是 false），供面板开关取值 */
  const enabled = ref(false);
  /** 是否为「部分房间开启」：有开启但没开满，面板据此显示“部分” */
  const partial = ref(false);
  /** 已开启托管的房间数（拼提示用） */
  const enabledCount = ref(0);
  /** 参与门槛金额（KillStartAmount，0 = 不限制） */
  const amount = ref(0);
  /** 可用房间数（仅用于展示/提示，不锁死交互） */
  const roomCount = ref(0);
  /** 是否已至少成功读到一次配置（保存时必须） */
  const configReady = ref(false);
  /**
   * 重名主播是否避让（不参与托管）。
   * 服务端字段 NoHostingOnDuplicateDealer，默认 false（默认走智能清理）；
   * 开启后重名房间「避让」。详情页的重名角标按它分两种文案。
   */
  const noHostingOnDuplicate = ref(false);
  /**
   * 智能清理模式（与「重名避让」二选一）：true = 不做重名避让，换主播时清掉其他房间同名残留锚点。
   * 服务端字段 SmartAnchorClean，默认 true。
   */
  const smartAnchorClean = ref(true);

  let config: KillPigConfigVO | null = null;
  let rooms: RoomVO[] = [];

  /**
   * 从服务端拉取权威状态。
   * 两个接口分别容错：房间列表失败时仍能显示/修改门槛金额，配置失败时仍能显示房间数。
   * @returns 配置是否成功拉到（保存前必须为 true，避免拿旧快照回写）
   */
  async function load(): Promise<boolean> {
    const [roomsRes, cfgRes] = await Promise.all([
      safeInvoke<{ [key: string]: RoomVO }>('GetRoomList'),
      safeInvoke<KillPigConfigVO>('GetKillPigConfig'),
    ]);

    if (roomsRes.ok && roomsRes.value) {
      rooms = [];
      for (const key in roomsRes.value) {
        const room = roomsRes.value[key];
        if (!room) continue;
        Object.setPrototypeOf(room, RoomVO.prototype);
        rooms.push(room);
      }
      roomCount.value = rooms.length;
      // 三种态：全关 / 部分开启 / 全开。enabled 保持“有开启即 true”，
      // 这样只改门槛金额时不会被误判成一次开关变更（那会全量重设所有房间）。
      enabledCount.value = rooms.filter((r) => !!r.OpenKillPig).length;
      enabled.value = enabledCount.value > 0;
      partial.value = enabledCount.value > 0 && enabledCount.value < rooms.length;
    } else {
      console.warn('[QuickKill] 读取房间列表失败:', roomsRes.ok ? '结果为空' : roomsRes.error);
    }

    if (cfgRes.ok && cfgRes.value) {
      config = cfgRes.value;
      configReady.value = true;
      amount.value = Number(config.KillStartAmount ?? 0);
      // 只有明确的 true 才算「避让」；字段缺失按服务端默认（不避让，走智能清理）
      noHostingOnDuplicate.value = config.NoHostingOnDuplicateDealer === true;
      smartAnchorClean.value = config.SmartAnchorClean !== false;
      // 房间列表没取到时，用配置里的总开关兜底（无法判断是否部分，不标“部分”）
      if (!roomsRes.ok) {
        enabled.value = !!config.OpenKillPig;
        partial.value = false;
        enabledCount.value = 0;
      }
      return true;
    }

    console.warn('[QuickKill] 读取托管配置失败:', cfgRes.ok ? '结果为空' : cfgRes.error);
    return false;
  }

  /**
   * 保存：只写「总控开关」（确实变化时）与「参与门槛金额」。
   *
   * 关键约束（避免覆盖其它配置）：
   * 1. 服务端 SetKillPigConfig 是**整体替换** Global.KillPigConfig，
   *    所以保存前必须重新拉一次最新配置，只改 KillStartAmount 再回写，
   *    不能用打开面板时的旧快照，否则会把别人刚改的字段覆盖回去。
   * 2. 只有开关真的发生变化时才调用 SetKillPig（它会重设所有房间的 OpenKillPig），
   *    否则只改金额也会把“部分房间开启”变成全部开启/全部关闭。
   */
  async function save(payload: { enabled: boolean; amount: number }): Promise<boolean> {
    const fresh = await load();

    // 没拿到最新配置就绝不写：SetKillPigConfig 是整体替换，旧快照会覆盖别人的修改
    if (!fresh || !config) {
      throw new Error('读取托管配置失败，请稍后重试');
    }

    const enabledChanged = payload.enabled !== enabled.value;

    if (enabledChanged) {
      if (payload.enabled && rooms.length === 0) {
        throw new Error('暂无可控房间，请先拉取数据');
      }
      const targetRooms = payload.enabled ? rooms.map((r) => r.RoomNumber) : [];
      const killOk = await SignalRClient.Instance.InvokeAsync<boolean>('SetKillPig', targetRooms);
      if (!killOk) {
        try {
          await load();
        } catch {
          /* ignore */
        }
        return false;
      }
    }

    // 只覆盖门槛金额，其它字段保持刚刚拉取到的最新值
    config.KillStartAmount = payload.amount;
    const configOk = await SignalRClient.Instance.InvokeAsync<boolean>('SetKillPigConfig', config);

    if (configOk) {
      // 仅开关变化时才动本地房间状态，避免把服务端的“部分开启”误标成全开
      if (enabledChanged) {
        // 开关是“全开/全关”二选一，写回后就是确定态，不再有“部分”
        for (const room of rooms) room.OpenKillPig = payload.enabled;
        enabledCount.value = payload.enabled ? rooms.length : 0;
        partial.value = false;
      }
      enabled.value = payload.enabled;
      amount.value = payload.amount;
      return true;
    }

    // 保存失败：回读服务端真实状态，别让界面停在错误草稿上
    try {
      await load();
    } catch {
      /* 回读失败忽略，界面保持原值 */
    }
    return false;
  }

  /**
   * 就地切换「重名主播是否避让」（详情页荷官角标上的按钮用，不必进数据面板）。
   *
   * 与 save() 同一约束：服务端 SetKillPigConfig 是整体替换，必须先拉最新配置，
   * 只改 NoHostingOnDuplicateDealer 这一个字段再回写，避免覆盖别人刚改的其它配置。
   *
   * @param avoid true = 避让（默认，重名房间不参与托管）；false = 照常（仍参与托管）
   * @returns 是否写入成功
   */
  async function setNoHostingOnDuplicate(avoid: boolean): Promise<boolean> {
    const fresh = await load();
    if (!fresh || !config) {
      throw new Error('读取托管配置失败，请稍后重试');
    }

    config.NoHostingOnDuplicateDealer = avoid;
    // 避让 / 照常 与「智能清理」二选一：从角标切换即退出智能清理模式
    config.SmartAnchorClean = false;
    const ok = await SignalRClient.Instance.InvokeAsync<boolean>('SetKillPigConfig', config);
    if (ok) {
      noHostingOnDuplicate.value = avoid;
      smartAnchorClean.value = false;
      return true;
    }

    // 写失败：回读服务端真实状态，界面不要停在错误草稿上
    try {
      await load();
    } catch {
      /* ignore */
    }
    return false;
  }

  /** 翻转重名策略（避让 ⇄ 照常） */
  async function toggleNoHostingOnDuplicate(): Promise<boolean> {
    return setNoHostingOnDuplicate(!noHostingOnDuplicate.value);
  }

  return {
    enabled, partial, enabledCount, amount, roomCount, configReady, noHostingOnDuplicate, smartAnchorClean,
    load, save, setNoHostingOnDuplicate, toggleNoHostingOnDuplicate,
  };
}
